import axios from 'axios'
import isEqual from 'lodash-es/isEqual'
import { reverse } from 'named-urls'
import type { ReactNode } from 'react'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Toasts, useModalContext } from '@fourthwall/components'
import { getDefaultErrorMessage } from '@fourthwall/utils/dist/queryClient'
import { routing } from '@utils/routing'

import { useCancellationOffers } from '../../hooks/useCancellationOffers'
import { useOrderIdParam } from '../../hooks/useOrderIdParam'
import { useOrderGroupedByShipmentsQuery } from '../../queries/useOrderGroupedByShipmentsQuery'
import type { ExpectedCostSplit } from '../OrderRefund/modules/OrderRefundConfirmModal'
import type { SelectableOffer } from './OrderCancel.types'
import { OrderCancelView } from './OrderCancelView'
import { OrderCancelConfirmModal } from './modules/OrderCancelConfirmModal'
import { useCalculateCancelRefundsQuery } from './queries/useCalculateCancelRefundsQuery'
import { useCancelOrderMutation } from './queries/useCancelOrderMutation'
import { handleCalculateRefundsError } from './utils/handleCalculateRefundsError'

export const OrderCancelContainer = () => {
  const navigate = useNavigate()
  const { open } = useModalContext()
  const { orderId } = useOrderIdParam()
  const { orderGroupedByShipmentsQuery } = useOrderGroupedByShipmentsQuery([{ orderId }])

  const [shouldRestock, setShouldRestock] = useState(true)
  const [itemsToCancel, setItemsToCancel] = useState<{ variantId: string; quantity: number }[]>([])
  const {
    cancellableOffers,
    nonCancellableOffers,
    partiallyCancellable,
    orderCancellationQuery,
    invalidateOrderCancellationQuery,
  } = useCancellationOffers(orderId)
  const [selectableOffers, setSelectableOffers] = useState<SelectableOffer[]>([])

  useEffect(() => {
    if (orderCancellationQuery.isSuccess && cancellableOffers.length) {
      setSelectableOffers(
        cancellableOffers.map((cancellableOffer) => ({
          ...cancellableOffer,
          selected: false,
          selectedQuantity: cancellableOffer.quantity,
        })),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderCancellationQuery.isSuccess])

  const { calculateCancelRefundsQuery } = useCalculateCancelRefundsQuery(
    [orderId, { itemsToCancel }],
    { retry: false, keepPreviousData: true, onError: handleCalculateRefundsError },
  )
  const { cancelOrderMutation } = useCancelOrderMutation({ onError: undefined })
  const calculateCancelRefunds = calculateCancelRefundsQuery.data
  const hasGiftCardsUsed = orderGroupedByShipmentsQuery.data?.giftCards
    ? orderGroupedByShipmentsQuery.data.giftCards.giftCards.length > 0
    : false
  const allItemsSelected = cancellableOffers
    ? itemsToCancel.length === cancellableOffers.length
    : false
  const cancellationAmountBiggerThanMaxRefund =
    !!calculateCancelRefunds &&
    calculateCancelRefunds.cancellationAmounts.total.value >
      calculateCancelRefunds.maxRefundAvailable.value

  const cannotRefundDueToGiftCards =
    hasGiftCardsUsed && cancellationAmountBiggerThanMaxRefund && !allItemsSelected

  useEffect(() => {
    if (!selectableOffers) {
      setItemsToCancel([])

      return
    }

    const itemsToCancel = selectableOffers
      .filter((selectableOffer) => selectableOffer.selected)
      .map(({ grouped, variantId, selectedQuantity }) => ({
        variantId,
        quantity: selectedQuantity,
        groupedId: grouped?.groupedId,
      }))

    setItemsToCancel(itemsToCancel)
  }, [selectableOffers])

  const getOrderCancellationReasons = () => {
    if (orderGroupedByShipmentsQuery.data?.salesChannel.type === 'TikTok') {
      return [
        { value: 'seller_cancel_reason_out_of_stock', label: 'Out of stock' },
        { value: 'seller_cancel_reason_wrong_price', label: 'Pricing error' },
        {
          value: 'seller_cancel_paid_reason_address_not_deliver',
          label: 'Unable to deliver to buyer address',
        },
        {
          value: 'seller_cancel_paid_reason_buyer_requested_cancellation',
          label: 'Buyer requested cancellation',
        },
      ]
    }
  }

  const cancelOrder = (
    productsContent: ReactNode,
    expectedCostSplit?: ExpectedCostSplit,
    reason?: string,
  ) => {
    cancelOrderMutation.mutate(
      [orderId, { itemsToCancel, restockCancelledItems: shouldRestock, reason, expectedCostSplit }],
      {
        onSuccess: () => {
          invalidateOrderCancellationQuery()
          navigate(reverse(routing.contributions.orders.order.self, { orderId }))
          Toasts.notify('Order has been canceled successfully.', { type: 'info' })
        },
        onError: (error: unknown) => {
          if (
            axios.isAxiosError(error) &&
            error.response?.data.code === 'ORDER_CANCEL_EXPECTED_CHARGE_AMOUNT_CHANGED'
          ) {
            open(OrderCancelConfirmModal, {
              orderId,
              itemsToCancel,
              content: productsContent,
              reasonOptions: getOrderCancellationReasons(),
              initialReason: reason,
              error: 'Your balance has changed and cannot cover refund amount. Please try again.',
              onConfirm: (expectedCostSplit, reason) => {
                cancelOrder(productsContent, expectedCostSplit, reason)
              },
            })

            return
          }
          Toasts.notify(getDefaultErrorMessage(error), { type: 'error' })
        },
      },
    )
  }

  const handleCancelOrder = (productsContent: ReactNode) => {
    open(OrderCancelConfirmModal, {
      orderId,
      itemsToCancel,
      content: productsContent,
      reasonOptions: getOrderCancellationReasons(),
      onConfirm: (expectedCostSplit, reason) =>
        cancelOrder(productsContent, expectedCostSplit, reason),
    })
  }

  const handleOfferSelect = (selectableOffer: SelectableOffer, selected: boolean) => {
    setSelectableOffers((currentSelectableOffers) =>
      currentSelectableOffers?.map((offer) => {
        if (
          selectableOffer.variantId === offer.variantId &&
          isEqual(selectableOffer.grouped, offer.grouped)
        ) {
          return { ...offer, selected }
        }

        return offer
      }),
    )
  }

  const handleOfferSelectedQuantityChange = (
    selectableOffer: SelectableOffer,
    selectedQuantity: number,
  ) => {
    setSelectableOffers((currentSelectableOffers) =>
      currentSelectableOffers?.map((offer) => {
        if (
          selectableOffer.variantId === offer.variantId &&
          isEqual(selectableOffer.grouped, offer.grouped)
        ) {
          return { ...offer, selectedQuantity }
        }

        return offer
      }),
    )
  }

  const handleSelectAll = (selected: boolean) => {
    setSelectableOffers((currentSelectableOffers) =>
      currentSelectableOffers?.map((offer) => {
        return { ...offer, selected }
      }),
    )
  }

  return (
    <OrderCancelView
      orderId={orderId}
      friendlyId={orderGroupedByShipmentsQuery.data?.friendlyId}
      cancelableOffers={selectableOffers}
      nonCancellableOffers={nonCancellableOffers}
      donation={orderGroupedByShipmentsQuery.data?.donation || undefined}
      promotion={orderGroupedByShipmentsQuery.data?.promotion}
      subtotal={calculateCancelRefunds?.cancellationAmounts.items}
      shipping={calculateCancelRefunds?.cancellationAmounts.shipping}
      taxes={calculateCancelRefunds?.cancellationAmounts.tax}
      discount={calculateCancelRefunds?.cancellationAmounts.discount}
      refundTotal={calculateCancelRefunds?.amountToRefund}
      shouldRestock={shouldRestock}
      isCancelButtonDisabled={
        !partiallyCancellable ||
        !selectableOffers?.filter((offer) => offer.selected).length ||
        calculateCancelRefundsQuery.isFetching ||
        calculateCancelRefundsQuery.isError ||
        orderCancellationQuery.isFetching ||
        orderCancellationQuery.isError ||
        cannotRefundDueToGiftCards
      }
      isLoading={
        orderGroupedByShipmentsQuery.isLoading ||
        calculateCancelRefundsQuery.isLoading ||
        calculateCancelRefundsQuery.isLoading ||
        cancelOrderMutation.isLoading
      }
      banner={
        cannotRefundDueToGiftCards ? (
          <>
            Selected items cannot be canceled because the refund amount exceeds the amount paid by
            supporter with the original payment method. Please{' '}
            <a href="mailto:support@fourthwall.com">contact customer support</a> to request a manual
            refund.
          </>
        ) : undefined
      }
      onOrderCancel={handleCancelOrder}
      onOfferSelect={handleOfferSelect}
      onRestockChange={setShouldRestock}
      onOfferSelectedQuantityChange={handleOfferSelectedQuantityChange}
      onSelectAll={handleSelectAll}
    />
  )
}
